import { Link } from 'react-router-dom'
import Seo from '../components/Seo'
import { SITE } from '../lib/site'
import { useLocale, localizedHref } from '../i18n/locale'

interface PainPoint { icon: string; title: string; body: string }
interface Step { n: string; title: string; body: string }
interface Example { img: string; scenario: string; challenge: string; result: string; cta: string; href: string }
interface Benefit { icon: string; title: string; body: string }

interface HomeContent {
  seoTitle: string
  seoDesc: string
  heroBadge: string
  heroH1a: string
  heroH1b: string
  heroLead: string
  ctaTry: string
  ctaPlanner: string
  heroChecks: string[]
  heroAlt: string
  painH2: string
  painSub: string
  pains: PainPoint[]
  solKicker: string
  solH2: string
  solLead: string
  solBullets: string[]
  solLink: string
  solAlt: string
  howH2: string
  howSub: string
  steps: Step[]
  exH2: string
  exSub: string
  challengeLabel: string
  getLabel: string
  examples: Example[]
  benH2: string
  benSub: string
  benefits: Benefit[]
  finalH2: string
  finalLead: string
  ctaBrowse: string
  finalNote: string
}

const EN: HomeContent = {
  seoTitle: 'RenoSheets — Free Home Renovation Material Calculators',
  seoDesc:
    'Stop guessing what to buy. RenoSheets gives you exact quantities plus a complete materials list for every renovation job — paint, tile, concrete and more. Free, no signup.',
  heroBadge: 'Free · No signup · Your data stays on your device',
  heroH1a: 'Stop guessing.',
  heroH1b: 'Start your reno right.',
  heroLead:
    'Free calculators that tell you exactly what to buy, plus a project planner to track every trade from first quote to final coat — all in one place, no signup required.',
  ctaTry: 'Try a Calculator →',
  ctaPlanner: 'Open the Planner',
  heroChecks: ['Free forever', 'No account needed', 'US & Canada', 'Works offline'],
  heroAlt: 'RenoSheets project planner and material calculator in use',
  painH2: 'Sound familiar?',
  painSub: 'These are the real costs of estimating by gut feel.',
  pains: [
    { icon: '🏪', title: 'Back to the store — again', body: 'You bought what looked like enough tile. It wasn\'t. Now the job is on hold while you hunt for a matching dye lot that may not exist.' },
    { icon: '📋', title: 'The missing items list', body: 'You measured the paint, but forgot primer, roller covers, tape, and tray liners. Four separate trips for a one-day job.' },
    { icon: '💸', title: 'Overbuying to play it safe', body: 'So you round way up to avoid trip two. Now you have 8 leftover bags of thinset and no way to return opened product.' },
  ],
  solKicker: 'The RenoSheets difference',
  solH2: 'One calculation. Everything you need.',
  solLead:
    'Most calculators give you one number. We give you the complete picture — every material, every consumable, every detail — so you walk into the store knowing exactly what to put in the cart.',
  solBullets: [
    'Exact quantities, not rough guesses',
    'Complete materials list — nothing missing',
    'Adjustable waste factors you can see and control',
    'Save results and build a shopping list',
    'Project planner to track your whole renovation',
  ],
  solLink: 'See how it works →',
  solAlt: 'RenoSheets complete materials list showing quantities for every item you need',
  howH2: 'How it works',
  howSub: 'From measurements to shopping list in under two minutes.',
  steps: [
    { n: '01', title: 'Enter your measurements', body: 'Type in your room dimensions — length, width, height — or add multiple areas at once. Works in feet or metres.' },
    { n: '02', title: 'Get your complete materials list', body: 'Every calculator outputs the primary material plus the consumables that go with it: grout, thinset, tape, primer, spacers, screws — nothing missing.' },
    { n: '03', title: 'Save, plan, and shop', body: 'Save any result to My Sheets for reference. Use the Project Planner to track your whole renovation — what\'s quoted, what\'s in progress, what\'s done.' },
  ],
  exH2: 'Real renovation scenarios',
  exSub: 'Here\'s what RenoSheets does for common projects.',
  challengeLabel: 'The challenge:',
  getLabel: 'What you get:',
  examples: [
    { img: '/images/scenario-tile.webp', scenario: 'Bathroom tile floor', challenge: 'How many 12×24 tiles for an 85 sq ft bathroom? What grout, thinset, and backer board?', result: 'Get exact tile count with waste, thinset bags adjusted for large-format tile, grout from joint-width formula, and backer board sheets — all in one list.', cta: 'Try Tile Calculator', href: '/calculators/tile/' },
    { img: '/images/scenario-paint.webp', scenario: 'Painting three bedrooms', challenge: 'Two coats, three rooms of different sizes. How many gallons of paint and primer?', result: 'Enter each room once, get total gallons by color, primer, roller covers, tape and trays — split by room so you can shop by color.', cta: 'Try Paint Calculator', href: '/calculators/paint/' },
    { img: '/images/scenario-kitchen.webp', scenario: 'Full kitchen renovation', challenge: 'Tile, paint, drywall, and new concrete countertop base — all happening at once. How do you stay organized?', result: 'Create a project in the Planner, add a task card for each trade, move them through Planning → Quoted → In Progress → Done as the job progresses.', cta: 'Open the Planner', href: '/kanban-board/' },
  ],
  benH2: 'Built for people who measure twice',
  benSub: 'Everything a serious renovator needs, nothing they don\'t.',
  benefits: [
    { icon: '📦', title: 'Complete lists, not just one number', body: 'Every result includes the full buy list — the primary material plus every consumable that goes with it.' },
    { icon: '🎯', title: 'Transparent, adjustable waste', body: 'See exactly what assumptions were used. Adjust waste %, number of coats, and more — no black-box padding.' },
    { icon: '🗂️', title: 'Project planner built in', body: 'Kanban board to track your whole renovation across Planning, Quoted, In Progress, and Done.' },
    { icon: '🔒', title: 'Your data stays in your browser', body: 'No accounts, no server. Everything you save lives in your browser\'s local storage. Private by design.' },
  ],
  finalH2: 'Ready to stop guessing?',
  finalLead: 'Pick a calculator, enter your measurements, and get a complete materials list in under two minutes. Free, forever.',
  ctaBrowse: 'Browse All Calculators',
  finalNote: 'No account. No download. Works in any browser.',
}

const FR: HomeContent = {
  seoTitle: 'RenoSheets — Calculateurs de matériaux de rénovation gratuits',
  seoDesc:
    'Arrêtez de deviner quoi acheter. RenoSheets vous donne les quantités exactes plus une liste complète de matériaux pour chaque projet de rénovation — peinture, carrelage, béton et plus. Gratuit, sans inscription.',
  heroBadge: 'Gratuit · Sans inscription · Vos données restent sur votre appareil',
  heroH1a: 'Arrêtez de deviner.',
  heroH1b: 'Partez votre réno du bon pied.',
  heroLead:
    'Des calculateurs gratuits qui vous disent exactement quoi acheter, plus un planificateur de projet pour suivre chaque métier, du premier devis à la dernière couche — le tout au même endroit, sans inscription.',
  ctaTry: 'Essayer un calculateur →',
  ctaPlanner: 'Ouvrir le planificateur',
  heroChecks: ['Gratuit pour toujours', 'Aucun compte requis', 'Canada et États-Unis', 'Fonctionne hors ligne'],
  heroAlt: 'Le planificateur de projet et le calculateur de matériaux RenoSheets en cours d’utilisation',
  painH2: 'Ça vous dit quelque chose?',
  painSub: 'Voici les vrais coûts d’une estimation à l’œil.',
  pains: [
    { icon: '🏪', title: 'Retour au magasin — encore', body: 'Vous aviez acheté ce qui semblait assez de carrelage. Ce ne l’était pas. Le chantier est en attente pendant que vous cherchez un lot de teinte identique qui n’existe peut-être plus.' },
    { icon: '📋', title: 'La liste d’oublis', body: 'Vous avez mesuré la peinture, mais oublié l’apprêt, les manchons, le ruban et les doublures de bac. Quatre allers-retours pour un travail d’une journée.' },
    { icon: '💸', title: 'Trop acheter par prudence', body: 'Alors vous arrondissez largement pour éviter un deuxième voyage. Vous voilà avec 8 sacs de mortier en trop, sans pouvoir retourner un produit ouvert.' },
  ],
  solKicker: 'La différence RenoSheets',
  solH2: 'Un seul calcul. Tout ce qu’il vous faut.',
  solLead:
    'La plupart des calculateurs donnent un seul chiffre. Nous donnons le portrait complet — chaque matériau, chaque consommable, chaque détail — pour que vous entriez au magasin en sachant exactement quoi mettre dans le panier.',
  solBullets: [
    'Des quantités exactes, pas des estimations approximatives',
    'Liste de matériaux complète — rien d’oublié',
    'Des marges de perte ajustables, visibles et sous votre contrôle',
    'Enregistrez les résultats et bâtissez une liste d’achats',
    'Un planificateur de projet pour suivre toute votre rénovation',
  ],
  solLink: 'Voir comment ça marche →',
  solAlt: 'Liste de matériaux complète de RenoSheets montrant les quantités pour chaque article nécessaire',
  howH2: 'Comment ça marche',
  howSub: 'Des mesures à la liste d’achats en moins de deux minutes.',
  steps: [
    { n: '01', title: 'Entrez vos mesures', body: 'Saisissez les dimensions de la pièce — longueur, largeur, hauteur — ou ajoutez plusieurs surfaces à la fois. En pieds ou en mètres.' },
    { n: '02', title: 'Obtenez votre liste complète de matériaux', body: 'Chaque calculateur affiche le matériau principal et les consommables qui l’accompagnent : coulis, mortier, ruban, apprêt, croisillons, vis — rien d’oublié.' },
    { n: '03', title: 'Enregistrez, planifiez et achetez', body: 'Enregistrez tout résultat dans Mes feuilles. Utilisez le planificateur de projet pour suivre votre rénovation — ce qui est en soumission, en cours, terminé.' },
  ],
  exH2: 'De vrais scénarios de rénovation',
  exSub: 'Voici ce que RenoSheets fait pour les projets courants.',
  challengeLabel: 'Le défi :',
  getLabel: 'Ce que vous obtenez :',
  examples: [
    { img: '/images/scenario-tile.webp', scenario: 'Plancher de céramique de salle de bain', challenge: 'Combien de tuiles 12×24 pour une salle de bain de 85 pi²? Quel coulis, mortier et panneau d’appui?', result: 'Obtenez le nombre exact de tuiles avec perte, les sacs de mortier ajustés pour grand format, le coulis selon la largeur des joints, et les feuilles de panneau d’appui — le tout dans une seule liste.', cta: 'Calculateur de carrelage', href: '/calculators/tile/' },
    { img: '/images/scenario-paint.webp', scenario: 'Peindre trois chambres', challenge: 'Deux couches, trois pièces de tailles différentes. Combien de gallons de peinture et d’apprêt?', result: 'Entrez chaque pièce une fois, obtenez le total en gallons par couleur, l’apprêt, les manchons, le ruban et les bacs — répartis par pièce pour magasiner par couleur.', cta: 'Calculateur de peinture', href: '/calculators/paint/' },
    { img: '/images/scenario-kitchen.webp', scenario: 'Rénovation complète de cuisine', challenge: 'Carrelage, peinture, gypse et nouvelle base de comptoir en béton — tout en même temps. Comment rester organisé?', result: 'Créez un projet dans le planificateur, ajoutez une carte de tâche pour chaque métier, et faites-les passer de Planification → Soumission → En cours → Terminé au fil du chantier.', cta: 'Ouvrir le planificateur', href: '/kanban-board/' },
  ],
  benH2: 'Conçu pour ceux qui mesurent deux fois',
  benSub: 'Tout ce dont un rénovateur sérieux a besoin, rien de superflu.',
  benefits: [
    { icon: '📦', title: 'Des listes complètes, pas qu’un chiffre', body: 'Chaque résultat inclut la liste d’achats complète — le matériau principal plus chaque consommable qui va avec.' },
    { icon: '🎯', title: 'Perte transparente et ajustable', body: 'Voyez exactement les hypothèses utilisées. Ajustez le % de perte, le nombre de couches et plus — aucune marge cachée.' },
    { icon: '🗂️', title: 'Planificateur de projet intégré', body: 'Un tableau Kanban pour suivre toute votre rénovation : Planification, Soumission, En cours et Terminé.' },
    { icon: '🔒', title: 'Vos données restent dans votre navigateur', body: 'Aucun compte, aucun serveur. Tout ce que vous enregistrez reste dans le stockage local de votre navigateur. Privé par conception.' },
  ],
  finalH2: 'Prêt à arrêter de deviner?',
  finalLead: 'Choisissez un calculateur, entrez vos mesures, et obtenez une liste complète de matériaux en moins de deux minutes. Gratuit, pour toujours.',
  ctaBrowse: 'Voir tous les calculateurs',
  finalNote: 'Aucun compte. Aucun téléchargement. Fonctionne dans tout navigateur.',
}

export default function Home() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)

  return (
    <>
      <Seo
        title={c.seoTitle}
        description={c.seoDesc}
        path="/"
        schema={{ '@context': 'https://schema.org', '@type': 'WebSite', name: SITE.name, url: SITE.url }}
      />

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-900 to-teal-900">
        <div className="mx-auto max-w-6xl px-4 py-16 md:py-24">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-4 py-1.5 text-sm text-teal-300">
                <span className="h-1.5 w-1.5 rounded-full bg-teal-400"></span>
                {c.heroBadge}
              </div>
              <h1 className="text-4xl font-bold leading-tight text-white md:text-5xl">
                {c.heroH1a}<br />
                <span className="text-teal-400">{c.heroH1b}</span>
              </h1>
              <p className="mt-4 text-lg leading-relaxed text-slate-300">{c.heroLead}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={L('/calculators/')} className="rounded-lg bg-primary px-6 py-3 text-base font-semibold text-on-primary shadow-lg transition-opacity hover:opacity-90">
                  {c.ctaTry}
                </Link>
                <Link to={L('/kanban-board/')} className="rounded-lg border border-white/30 bg-white/5 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-white/10">
                  {c.ctaPlanner}
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-400">
                {c.heroChecks.map((label) => (
                  <span key={label} className="flex items-center gap-1"><span className="text-teal-400">✓</span> {label}</span>
                ))}
              </div>
            </div>
            <div className="hidden md:block">
              <img src="/images/hero.webp" alt={c.heroAlt} width={800} height={600} fetchPriority="high" decoding="async" className="rounded-2xl shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ── PROBLEM ───────────────────────────────────────────────────── */}
      <section className="border-b border-border bg-surface">
        <div className="mx-auto max-w-5xl px-4 py-16">
          <div className="text-center">
            <h2 className="text-3xl">{c.painH2}</h2>
            <p className="mt-2 text-text-muted">{c.painSub}</p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {c.pains.map((p, i) => (
              <div key={p.title} className="overflow-hidden rounded-xl border border-border bg-surface-2">
                <img src={`/images/pain-0${i + 1}.webp`} alt={p.title} width={560} height={300} loading="lazy" decoding="async" className="w-full object-cover" />
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-text">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-text-muted">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SOLUTION ──────────────────────────────────────────────────── */}
      <section className="border-b border-border bg-brand-tint/40">
        <div className="mx-auto max-w-5xl px-4 py-16">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <div className="text-sm font-semibold uppercase tracking-wider text-brand-text">{c.solKicker}</div>
              <h2 className="mt-2 text-3xl">{c.solH2}</h2>
              <p className="mt-4 text-text-muted">{c.solLead}</p>
              <ul className="mt-6 space-y-3">
                {c.solBullets.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-text-body">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand text-xs text-white">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to={L('/how-to-use/')} className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-brand-text hover:underline">
                {c.solLink}
              </Link>
            </div>
            <img src="/images/solution.webp" alt={c.solAlt} width={640} height={480} loading="lazy" decoding="async" className="rounded-2xl shadow-md w-full" />
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────────────────── */}
      <section className="border-b border-border bg-surface">
        <div className="mx-auto max-w-5xl px-4 py-16">
          <div className="text-center">
            <h2 className="text-3xl">{c.howH2}</h2>
            <p className="mt-2 text-text-muted">{c.howSub}</p>
          </div>
          <div className="mt-12 space-y-14">
            {c.steps.map((s, i) => (
              <div key={s.n} className={`grid gap-8 md:grid-cols-2 md:items-center ${i % 2 === 1 ? 'md:[&>*:first-child]:order-2' : ''}`}>
                <div>
                  <div aria-hidden="true" className="text-5xl font-bold text-brand opacity-30">{s.n}</div>
                  <h3 className="mt-1 text-xl font-semibold text-text">{s.title}</h3>
                  <p className="mt-3 leading-relaxed text-text-muted">{s.body}</p>
                </div>
                <img src={`/images/step-${s.n}.webp`} alt={s.title} width={560} height={380} loading="lazy" decoding="async" className="rounded-xl shadow-md w-full" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── USAGE EXAMPLES ────────────────────────────────────────────── */}
      <section className="border-b border-border bg-surface-2">
        <div className="mx-auto max-w-5xl px-4 py-16">
          <div className="text-center">
            <h2 className="text-3xl">{c.exH2}</h2>
            <p className="mt-2 text-text-muted">{c.exSub}</p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {c.examples.map((ex) => (
              <div key={ex.scenario} className="flex flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
                <div className="aspect-[4/3] w-full overflow-hidden bg-slate-100">
                  <img src={ex.img} alt={ex.scenario} width={800} height={600} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-semibold text-text">{ex.scenario}</h3>
                  <p className="mt-2 text-sm text-text-muted"><strong className="text-text-body">{c.challengeLabel}</strong> {ex.challenge}</p>
                  <p className="mt-2 text-sm text-text-muted"><strong className="text-text-body">{c.getLabel}</strong> {ex.result}</p>
                  <div className="mt-auto pt-5">
                    <Link to={L(ex.href)} className="inline-block rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-on-primary transition-opacity hover:opacity-90">
                      {ex.cta}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WORK SMARTER ──────────────────────────────────────────────── */}
      <section className="border-b border-border bg-slate-900">
        <div className="mx-auto max-w-5xl px-4 py-16">
          <div className="text-center">
            <h2 className="text-3xl text-white">{c.benH2}</h2>
            <p className="mt-2 text-slate-400">{c.benSub}</p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {c.benefits.map((b) => (
              <div key={b.title} className="rounded-xl border border-white/10 bg-white/5 p-6">
                <div className="text-3xl">{b.icon}</div>
                <h3 className="mt-3 font-semibold text-white">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────────────────── */}
      <section className="bg-surface">
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <h2 className="text-3xl">{c.finalH2}</h2>
          <p className="mx-auto mt-3 max-w-xl text-text-muted">{c.finalLead}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to={L('/calculators/')} className="rounded-lg bg-primary px-8 py-3.5 text-base font-semibold text-on-primary shadow-md transition-opacity hover:opacity-90">
              {c.ctaBrowse}
            </Link>
            <Link to={L('/kanban-board/')} className="rounded-lg border-2 border-brand px-8 py-3.5 text-base font-semibold text-brand-text transition-colors hover:bg-brand-tint">
              {c.ctaPlanner}
            </Link>
          </div>
          <p className="mt-6 text-sm text-text-muted">{c.finalNote} {SITE.tagline}</p>
        </div>
      </section>
    </>
  )
}
